/**
 * Ability-bar keys and the intent an ability press sends (combat.md §Control model;
 * combat-depth.md §3.2). Pure, so the HUD wiring is testable under `node --test`.
 *
 * Keys `1`–`6` press ability slots 1–6 — the seven-button ceiling's six ability
 * slots (dodge is the seventh; the basic attack is the default tap, not a slot).
 * No other binding uses a digit (panel hotkeys are letters / F-keys / backtick,
 * capture keys are F3/F4/[ ]/\). Typing is guarded by the caller (`isGameKey`).
 */

import type { AbilityIntentPayload } from '@great-cloud/shared/client';

/** The ability-bar slot count bound to digit keys (6 ability slots, combat.md). */
export const ABILITY_HOTKEY_SLOTS = 6;

/** Slot index (0-based) for a `KeyboardEvent.key`, or null when it is not an ability key. */
export function abilitySlotForKey(key: string): number | null {
  if (key.length !== 1 || key < '1' || key > '9') return null;
  const slot = key.charCodeAt(0) - 49; // '1' → 0
  return slot < ABILITY_HOTKEY_SLOTS ? slot : null;
}

/**
 * The `abilityIntent` payload for a press: the soft target's id when there is one
 * (without it a damage effect does nothing server-side — the defect this fixes),
 * otherwise the bare ability id, exactly as before.
 */
export function abilityIntentFor(
  abilityId: string,
  softTarget: { id: number } | null,
): AbilityIntentPayload {
  return softTarget === null ? { abilityId } : { abilityId, targetEntityId: softTarget.id };
}

/**
 * The ONE ability press: keys `1`–`6`, the HUD bar's slots and the touch overlay's slots
 * (mobile-layout.md §7.2) all call this, so each names the soft target picked AT the press —
 * the explicit (tapped/clicked) target first while it is legal (AD-817), then threat, then
 * nearest. The server still judges range, stamina and cooldown.
 */
export function sendAbilityPress(
  send: (type: 'abilityIntent', payload: AbilityIntentPayload) => void,
  softTarget: () => { id: number } | null,
  abilityId: string,
): void {
  send('abilityIntent', abilityIntentFor(abilityId, softTarget()));
}
